import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getLeadhoopAccounts,
  listLeadhoopSyncRuns,
  startLeadhoopBackfill,
  triggerLeadhoopSync,
} from "@/api/leadhoop";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { Spinner } from "@/components/Spinner";
import { fmtDateTime, fmtDuration, fmtNumber } from "@/lib/format";

const STATUS_COLOURS: Record<string, string> = {
  ok: "bg-emerald-100 text-emerald-800",
  running: "bg-blue-100 text-blue-800",
  error: "bg-red-100 text-red-800",
  partial: "bg-amber-100 text-amber-800",
};

export function LeadhoopSyncRuns() {
  const qc = useQueryClient();
  const { data: accounts } = useQuery({
    queryKey: ["leadhoop", "accounts"],
    queryFn: getLeadhoopAccounts,
  });

  const [filterAccount, setFilterAccount] = useState<string>("");
  const [filterSession, setFilterSession] = useState<string>("");

  const [triggerAccount, setTriggerAccount] = useState<string>("");
  const [triggerSession, setTriggerSession] = useState<string>("");

  const [backfillAccount, setBackfillAccount] = useState<string>("");
  const [backfillSession, setBackfillSession] = useState<string>("");
  const [backfillFrom, setBackfillFrom] = useState<string>("");
  const [backfillTo, setBackfillTo] = useState<string>("");

  const { data: runs, isLoading, error } = useQuery({
    queryKey: [
      "leadhoop",
      "sync-runs",
      filterAccount,
      filterSession,
    ],
    queryFn: () =>
      listLeadhoopSyncRuns({
        account_slug: filterAccount || undefined,
        publisher_session_id: filterSession
          ? Number(filterSession)
          : undefined,
        limit: 100,
      }),
  });

  const triggerMut = useMutation({
    mutationFn: triggerLeadhoopSync,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leadhoop", "sync-runs"] }),
  });
  const backfillMut = useMutation({
    mutationFn: startLeadhoopBackfill,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leadhoop", "sync-runs"] }),
  });

  const triggerSessionOptions = useMemo(() => {
    if (!triggerAccount || !accounts) return [] as number[];
    return (
      accounts.find((a) => a.slug === triggerAccount)?.publisher_session_ids ??
      []
    );
  }, [accounts, triggerAccount]);

  const backfillSessionOptions = useMemo(() => {
    if (!backfillAccount || !accounts) return [] as number[];
    return (
      accounts.find((a) => a.slug === backfillAccount)?.publisher_session_ids ??
      []
    );
  }, [accounts, backfillAccount]);

  const filterSessionOptions = useMemo(() => {
    if (!filterAccount || !accounts) return [] as number[];
    return (
      accounts.find((a) => a.slug === filterAccount)?.publisher_session_ids ??
      []
    );
  }, [accounts, filterAccount]);

  function onTrigger(e: React.FormEvent) {
    e.preventDefault();
    if (!triggerAccount || !triggerSession) return;
    triggerMut.mutate({
      account_slug: triggerAccount,
      publisher_session_id: Number(triggerSession),
    });
  }

  function onBackfill(e: React.FormEvent) {
    e.preventDefault();
    if (
      !backfillAccount ||
      !backfillSession ||
      !backfillFrom ||
      !backfillTo
    )
      return;
    backfillMut.mutate({
      account_slug: backfillAccount,
      publisher_session_id: Number(backfillSession),
      from_date: backfillFrom,
      to_date: backfillTo,
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="LeadHoop sync runs"
        subtitle="Worker history + manual triggers + backfill"
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <form onSubmit={onTrigger} className="card p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">
            Trigger a realtime tick now
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Account</label>
              <select
                className="input"
                value={triggerAccount}
                onChange={(e) => {
                  setTriggerAccount(e.target.value);
                  setTriggerSession("");
                }}
              >
                <option value="">Select…</option>
                {accounts?.map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.slug}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Publisher session</label>
              <select
                className="input"
                value={triggerSession}
                onChange={(e) => setTriggerSession(e.target.value)}
                disabled={!triggerAccount}
              >
                <option value="">Select…</option>
                {triggerSessionOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="submit"
              className="btn-primary"
              disabled={
                !triggerAccount ||
                !triggerSession ||
                triggerMut.isPending
              }
            >
              {triggerMut.isPending && <Spinner size="sm" />}
              Run sync now
            </button>
            {triggerMut.isSuccess && (
              <span className="text-sm text-emerald-700">Queued ✓</span>
            )}
          </div>
          {triggerMut.error && <ErrorBanner error={triggerMut.error} />}
        </form>

        <form onSubmit={onBackfill} className="card p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">
            Backfill a date range
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Account</label>
              <select
                className="input"
                value={backfillAccount}
                onChange={(e) => {
                  setBackfillAccount(e.target.value);
                  setBackfillSession("");
                }}
              >
                <option value="">Select…</option>
                {accounts?.map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.slug}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Publisher session</label>
              <select
                className="input"
                value={backfillSession}
                onChange={(e) => setBackfillSession(e.target.value)}
                disabled={!backfillAccount}
              >
                <option value="">Select…</option>
                {backfillSessionOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">From</label>
              <input
                className="input"
                type="date"
                value={backfillFrom}
                onChange={(e) => setBackfillFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="label">To</label>
              <input
                className="input"
                type="date"
                value={backfillTo}
                onChange={(e) => setBackfillTo(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="submit"
              className="btn-primary"
              disabled={
                !backfillAccount ||
                !backfillSession ||
                !backfillFrom ||
                !backfillTo ||
                backfillMut.isPending
              }
            >
              {backfillMut.isPending && <Spinner size="sm" />}
              Start backfill
            </button>
            {backfillMut.isSuccess && (
              <span className="text-sm text-emerald-700">Started ✓</span>
            )}
          </div>
          {backfillMut.error && <ErrorBanner error={backfillMut.error} />}
        </form>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 p-4">
          <div>
            <label className="label">Filter account</label>
            <select
              className="input"
              value={filterAccount}
              onChange={(e) => {
                setFilterAccount(e.target.value);
                setFilterSession("");
              }}
            >
              <option value="">All</option>
              {accounts?.map((a) => (
                <option key={a.slug} value={a.slug}>
                  {a.slug}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Filter session</label>
            <select
              className="input"
              value={filterSession}
              onChange={(e) => setFilterSession(e.target.value)}
              disabled={!filterAccount}
            >
              <option value="">All</option>
              {filterSessionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <ErrorBanner error={error} />}

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Duration</th>
                <th>Account</th>
                <th>Session</th>
                <th>Kind</th>
                <th>Status</th>
                <th className="text-right">Fetched</th>
                <th className="text-right">Inserted</th>
                <th className="text-right">Updated</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={10} className="p-6 text-center text-slate-500">
                    <Spinner /> Loading…
                  </td>
                </tr>
              )}
              {runs?.map((r) => (
                <tr key={r.id}>
                  <td className="whitespace-nowrap">
                    {fmtDateTime(r.started_at)}
                  </td>
                  <td>{fmtDuration(r.started_at, r.finished_at)}</td>
                  <td>{r.account_slug}</td>
                  <td>{r.publisher_session_id}</td>
                  <td>{r.kind}</td>
                  <td>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOURS[r.status] ?? "bg-slate-100 text-slate-700"}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="text-right tabular-nums">
                    {fmtNumber(r.rows_fetched)}
                  </td>
                  <td className="text-right tabular-nums">
                    {fmtNumber(r.rows_inserted)}
                  </td>
                  <td className="text-right tabular-nums">
                    {fmtNumber(r.rows_updated)}
                  </td>
                  <td
                    className="max-w-xs truncate text-xs text-red-700"
                    title={r.error_message ?? undefined}
                  >
                    {r.error_message ?? ""}
                  </td>
                </tr>
              ))}
              {!isLoading && runs?.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-6 text-center text-slate-500">
                    No runs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
